import { TFunction } from "next-i18next";
import { withCdnUrl } from "@smile2impress/attraction-utils";
import {
    UTSLocaleToCountryVersion,
    UTSLocaleToLanguage,
} from "@smile2impress/attraction-constants";

import { mergeContent } from "utils/mergeContent";
import { PageContentProps } from "content/content.types";
import {
    OFFERS_AND_DISCOUNTS_LINKS_BY_LOCALE,
    PRIVACY_POLICY_LINKS_BY_LOCALE,
    UrlsConfig,
} from "configs/urlsConfig";
import contacts from "configs/contacts";
import { ContactTypes, SocialTypes } from "constants/enum";
import { getWALink } from "utils/getWALink";

import defaultContent from "./common";

const pageContent = (t: TFunction, { locale }: PageContentProps) => {
    const content = defaultContent(t, { locale });

    return mergeContent(content, {
        header: {
            menuTitle: t("navigationMenuRelaunch:navigationMenu"),
            logoLink: UrlsConfig.MAIN,
            items: [
                {
                    text: t("navigationMenuRelaunch:navigationTreatments"),
                    highlighted: true,
                    items: [
                        {
                            text: t(
                                "navigationMenuRelaunch:navigationAligners"
                            ),
                            link: UrlsConfig.ALIGNERS,
                            image: {
                                images: [
                                    {
                                        png: withCdnUrl(
                                            "/images/relaunch/common/navigation/aligners.png"
                                        ),
                                    },
                                ],
                                defaultSrc: withCdnUrl(
                                    "/images/relaunch/common/navigation/aligners.png"
                                ),
                            },
                        },
                        {
                            text: t("navigationMenuRelaunch:navigationTeens"),
                            link: UrlsConfig.TEENS,
                            image: {
                                images: [
                                    {
                                        png: withCdnUrl(
                                            "/images/relaunch/common/navigation/teens.png"
                                        ),
                                    },
                                ],
                                defaultSrc: withCdnUrl(
                                    "/images/relaunch/common/navigation/teens.png"
                                ),
                            },
                        },
                        {
                            text: t(
                                "navigationMenuRelaunch:navigationRetainers"
                            ),
                            link: UrlsConfig.RETAINERS_SUBSCRIPTION,
                            image: {
                                images: [
                                    {
                                        png: withCdnUrl(
                                            "/images/relaunch/common/navigation/retainers.png"
                                        ),
                                    },
                                ],
                                defaultSrc: withCdnUrl(
                                    "/images/relaunch/common/navigation/retainers.png"
                                ),
                            },
                        },
                    ],
                },
                {
                    text: t("navigationMenuRelaunch:navigationAesthetic"),
                    items: [
                        {
                            text: t(
                                "navigationMenuRelaunch:navigationWhitening"
                            ),
                            link: UrlsConfig.WHITENING,
                            image: {
                                images: [
                                    {
                                        png: withCdnUrl(
                                            "/images/relaunch/common/navigation/whitening.png"
                                        ),
                                    },
                                ],
                                defaultSrc: withCdnUrl(
                                    "/images/relaunch/common/navigation/whitening.png"
                                ),
                            },
                        },
                        {
                            text: t("navigationMenuRelaunch:navigationVeneers"),
                            link: "https://getstarted.smile2impress.com/pages/veneers",
                            image: {
                                images: [
                                    {
                                        png: withCdnUrl(
                                            "/images/relaunch/common/navigation/veneers.png"
                                        ),
                                    },
                                ],
                                defaultSrc: withCdnUrl(
                                    "/images/relaunch/common/navigation/veneers.png"
                                ),
                            },
                        },
                    ],
                },
                {
                    text: t("navigationMenuRelaunch:navigationWhy"),
                    link: UrlsConfig.WHY_IMPRESS,
                    image: {
                        images: [
                            {
                                png: withCdnUrl(
                                    "/images/relaunch/common/navigation/whyImpress.png"
                                ),
                            },
                        ],
                        defaultSrc: withCdnUrl(
                            "/images/relaunch/common/navigation/whyImpress.png"
                        ),
                    },
                },
                {
                    text: t("navigationMenuRelaunch:navigationPricing"),
                    link: UrlsConfig.PRICES,
                    image: {
                        images: [
                            {
                                png: withCdnUrl(
                                    "/images/relaunch/common/navigation/prices.png"
                                ),
                            },
                        ],
                        defaultSrc: withCdnUrl(
                            "/images/relaunch/common/navigation/prices.png"
                        ),
                    },
                },
                {
                    text: t("navigationMenuRelaunch:navigationClinics"),
                    link: UrlsConfig.LOCATIONS,
                    image: {
                        images: [
                            {
                                png: withCdnUrl(
                                    "/images/relaunch/common/navigation/locations.png"
                                ),
                            },
                        ],
                        defaultSrc: withCdnUrl(
                            "/images/relaunch/common/navigation/locations.png"
                        ),
                    },
                },
                {
                    text: t("navigationMenuRelaunch:navigationResults"),
                    link: UrlsConfig.RESULTS,
                    image: {
                        images: [
                            {
                                png: withCdnUrl(
                                    "/images/relaunch/common/navigation/results.png"
                                ),
                            },
                        ],
                        defaultSrc: withCdnUrl(
                            "/images/relaunch/common/navigation/results.png"
                        ),
                    },
                },
                {
                    text: t("navigationMenuRelaunch:navigationBlog"),
                    link: UrlsConfig.BLOG,
                    image: {
                        images: [
                            {
                                png: withCdnUrl(
                                    "/images/relaunch/common/navigation/blog.png"
                                ),
                            },
                        ],
                        defaultSrc: withCdnUrl(
                            "/images/relaunch/common/navigation/blog.png"
                        ),
                    },
                },
            ],
        },
        contacts: {
            wa: {
                text: t("navigationMenuRelaunch:navigationWhatsApp"),
                link: contacts.whatsapp[locale].link,
                newPatientsLink: (contacts.whatsapp[locale] as any)
                    .newPatientsLink,
                message: t("global:whatsappMessage"),
            },
        },
        footer: {
            company: {
                links: [
                    {
                        id: "whyImpress",
                        name: t("global:whyImpressPageTitle"),
                        link: UrlsConfig.WHY_IMPRESS,
                    },
                    {
                        id: "press",
                        name: t("global:pressPageTitle"),
                        link: `mailto:${contacts.emailPress.link}`,
                    },
                    {
                        id: "careers",
                        name: t("global:careersPageTitle"),
                        link: "https://www.linkedin.com/company/impress-orthodontics/jobs/",
                        target: "_blank",
                    },
                    {
                        id: "blog",
                        name: t("global:blogPageTitle"),
                        link: UrlsConfig.BLOG,
                        target: "_self",
                    },
                    {
                        id: "offersAndDiscounts",
                        name: t("navigationMenuRelaunch:offersAndDiscounts"),
                        link: OFFERS_AND_DISCOUNTS_LINKS_BY_LOCALE[locale],
                    },
                    {
                        id: "studentbeans",
                        name: t("navigationMenuRelaunch:studentBeans"),
                        link: UrlsConfig.STUDENT_BEANS,
                    },
                ],
            },
            legal: {
                title: t("global:legalNoticeFooterTitle"),
                links: [
                    {
                        id: "privacyPolicyPage",
                        name: t("global:privacyPolicy"),
                        link: PRIVACY_POLICY_LINKS_BY_LOCALE[locale],
                    },
                    {
                        id: "cookiesPage",
                        name: t("global:cookiesPolicy"),
                        link: withCdnUrl(
                            `${UrlsConfig.POLICY}/cookies-policy_${UTSLocaleToCountryVersion[locale]}.pdf`
                        ),
                    },
                    {
                        id: "legalNoticePage",
                        name: t("global:legalNoticeFooterText"),
                        link: withCdnUrl(
                            `${UrlsConfig.POLICY}/aviso-legal_${UTSLocaleToCountryVersion[locale]}.pdf`
                        ),
                    },
                    {
                        id: "complaintsPolicy",
                        name: t("global:complaintsPolicy"),
                        link: "https://smile2impress-5970216.hs-sites.com/uk/website/live/media/policy/complaints-policy_uk",
                    },
                ],
            },
            product: {
                title: t("global:productFooterText"),
                links: [
                    {
                        id: "aligners",
                        name: t("global:alignersPageTitle"),
                        link: UrlsConfig.ALIGNERS,
                    },
                    {
                        id: "teens",
                        name: t("global:teensPageTitle"),
                        link: UrlsConfig.TEENS,
                    },
                    {
                        name: t("navigationMenuRelaunch:clusterBOverbite"),
                        link: UrlsConfig.OVERBITE,
                    },
                    {
                        name: t("navigationMenuRelaunch:clusterBCrookedTeeth"),
                        link: UrlsConfig.CROOKED_TEETH,
                    },
                    {
                        name: t("navigationMenuRelaunch:clusterBDiastema"),
                        link: UrlsConfig.DIASTEMA,
                    },
                    {
                        name: t("navigationMenuRelaunch:clusterBCrossbite"),
                        link: UrlsConfig.CROSSBITE,
                    },
                    {
                        name: t("navigationMenuRelaunch:clusterBCrowding"),
                        link: UrlsConfig.CROWDING,
                    },
                ],
            },
            support: {
                title: t("global:supportFooterText"),
                links: [
                    {
                        id: "faq",
                        name: t("global:faqFooterText"),
                        link: `https://faq.smile2impress.com/${UTSLocaleToLanguage[locale]}`,
                    },
                    {
                        id: ContactTypes.PHONE,
                        name: contacts.phone[locale].public,
                        link:
                            contacts.phone[locale].link &&
                            `tel:${contacts.phone[locale].link}`,
                    },
                    {
                        id: SocialTypes.WA,
                        name: "WhatsApp",
                        link: getWALink(
                            (contacts.whatsapp[locale] as any).newPatientsLink,
                            t("global:whatsappMessage")
                        ),
                    },
                    {
                        id: SocialTypes.TG,
                        name: "Telegram",
                        link: contacts.telegram[locale].link,
                    },
                    {
                        id: ContactTypes.EMAIL,
                        name: t("global:email"),
                        link:
                            contacts.email[locale].link &&
                            `mailto:${contacts.email[locale].link}`,
                    },
                ],
            },
        },
    });
};

export default pageContent;
